/* eslint-disable react/destructuring-assignment */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import styled from 'styled-components';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';
import { useSelector, useDispatch } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import throttle from 'lodash/throttle';
import {
  fetchMoreMetaBooths,
  selectBoothPageInfo,
  selectTotalFilteredBoothsCount,
} from 'models/cms/booths';

import FloatingFooter from 'components/footer_v2/FloatingFooter';
import HiddenFooterContainer from 'components/footer_v2/HiddenFooterContainer';
import { CMSFetchMoreIndicator } from './ListRow';
import ListComponent from './ListComponent';
import BoothTierGroup from './BoothTierGroup';
import HeaderComponent from './HeaderComponent';

const HallPageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const BoothListContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  height: 100%;
  width: 100%;
`;

const LazyBoothList = ({
  exhibitorIds = [],
  exhibitorIdsGroupedByTierIds = {},
  isFiltered,
  hasFilter,
}: {
  isFiltered: boolean;
  exhibitorIds: string[];
  exhibitorIdsGroupedByTierIds: { [key: string]: Array<string> };
  hasFilter: boolean;
}) => {
  const dispatch = useDispatch();
  const [scrollableTarget, setScrollableTarget] = useState(null);
  const { features } = useSelector((s: any) => s.eventConfigs);
  const pageInfo = useSelector(selectBoothPageInfo);
  const totalFilteredBoothsCount = useSelector(selectTotalFilteredBoothsCount);
  const scrollableTargetRef = useRef();

  const onListScroll = useCallback(() => {
    if (!scrollableTarget || pageInfo.fetching) return;

    const { clientHeight, scrollHeight, scrollTop } = scrollableTarget;

    if (scrollTop + clientHeight > scrollHeight - 200) {
      dispatch(fetchMoreMetaBooths());
    }
  }, [dispatch, pageInfo.fetching, scrollableTarget]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onListScrollThrotte = useCallback(throttle(onListScroll, 500, { leading: false }), [scrollableTarget, pageInfo]);

  useEffect(() => {
    setScrollableTarget(scrollableTargetRef.current);
  }, []);

  useEffect(() => {
    if (!scrollableTarget) return;

    const isScrollbarHidden = scrollableTarget.clientHeight >= scrollableTarget.scrollHeight;

    if (isScrollbarHidden && pageInfo.hasNextPage && !pageInfo.fetching) {
      console.log('scrollableTarget clientHeight', scrollableTarget.clientHeight);
      console.log('scrollableTarget scrollHeight', scrollableTarget.scrollHeight);
      console.log('isScrollbarHidden', isScrollbarHidden);
      dispatch(fetchMoreMetaBooths());
    }
  }, [dispatch, pageInfo.hasNextPage, pageInfo.fetching, scrollableTarget]);

  const exhibitorIdsGroups = useMemo(() => (
    Object.values(exhibitorIdsGroupedByTierIds).map((ids) => (
      ids.map((id, index) => ({ id, index }))
    ))
  ), [exhibitorIdsGroupedByTierIds]);

  if (features.includes('boothTiers')) {
    return (
      <ScrollableContainerWrapper
        WrapperContainer={HallPageContainer}
        onScroll={onListScrollThrotte}
        refOverride={scrollableTargetRef}
      >
        <HeaderComponent
          isFiltered={isFiltered}
          hasFilter={hasFilter}
          filterLength={totalFilteredBoothsCount}
          headerOffset={0}
        />
        {
          !!exhibitorIdsGroups.length && exhibitorIdsGroups.map((ids, index) => (
            <BoothTierGroup key={index} exhibitorIdsWithIndex={ids} />
          ))
        }
        <CMSFetchMoreIndicator offsetTop={0} />
        <HiddenFooterContainer>
          <FloatingFooter />
        </HiddenFooterContainer>
      </ScrollableContainerWrapper>
    );
  }
  return (
    <BoothListContainer>
      <AutoSizer itemCount={exhibitorIds.length}>
        {({ height, width }) => (
          <ListComponent
            height={height}
            width={width}
            hasFilter={hasFilter}
            isFiltered={isFiltered}
            exhibitorIds={exhibitorIds}
          />
        )}
      </AutoSizer>
    </BoothListContainer>
  );
};

export default LazyBoothList;
